// assets/js/gsap.js

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

window.gsap = gsap;

const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

function prefersReducedMotion() {
    return reduceMotionQuery.matches;
}

/**
 * Every reveal that has been prepared but not played yet.
 * Used by the recovery sweep after a tab restore, when ScrollTrigger may have
 * measured the page against a viewport that no longer exists.
 */
const pendingReveals = [];

/**
 * Prepares one reveal.
 *
 * The initial state is written with gsap.set() and the reveal itself is a
 * plain gsap.to() fired from a one-shot ScrollTrigger. This is deliberate:
 * a `gsap.from()` that owns a ScrollTrigger is reverted to its start state on
 * every ScrollTrigger.refresh(), and a refresh happens automatically when the
 * browser is backgrounded and the viewport height changes. That is what pushes
 * already-revealed content back to opacity 0 with no trigger left to undo it.
 *
 * @param {Element|Element[]} targets  What actually animates.
 * @param {Element}           trigger  Element whose position starts the reveal.
 * @param {Object}            from     Extra start properties (x / y).
 * @param {Object}            options  { stagger, duration, playNow }
 */
function createReveal(targets, trigger, from, options) {

    const opts = options || {};
    const items = Array.isArray(targets) ? targets : [targets];

    if (!items.length) return;

    if (prefersReducedMotion()) return;

    gsap.set(items, Object.assign({ autoAlpha: 0 }, from));

    let played = false;

    const play = function () {

        if (played) return;

        played = true;

        const index = pendingReveals.indexOf(entry);

        if (index > -1) {
            pendingReveals.splice(index, 1);
        }

        gsap.to(items, {
            autoAlpha: 1,
            x: 0,
            y: 0,
            duration: opts.duration || 1,
            stagger: opts.stagger || 0,
            ease: "power2.out",
            overwrite: "auto",
            clearProps: "opacity,visibility,transform"
        });
    };

    const entry = { trigger: trigger, play: play };

    if (opts.playNow) {
        play();
        return;
    }

    pendingReveals.push(entry);

    ScrollTrigger.create({
        trigger: trigger,
        start: opts.start || "top bottom",
        once: true,
        onEnter: play
    });
}

/**
 * Reveals anything that is at or above the bottom of the viewport but still
 * waiting for a trigger. Runs after a tab restore or a bfcache restore, where
 * trigger positions can be stale enough that scrolling never crosses them.
 */
function revealVisiblePending() {

    if (!pendingReveals.length) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    pendingReveals.slice().forEach(function (entry) {

        if (!entry.trigger || !entry.trigger.isConnected) {
            entry.play();
            return;
        }

        const rect = entry.trigger.getBoundingClientRect();

        if (rect.top < viewportHeight) {
            entry.play();
        }
    });
}

window.revealVisiblePending = revealVisiblePending;

window.initFadeUpAnimations = function (scope = document, animateNow = false) {

    const animations = [
        { name: "fade-up", group: "group-fade-up", from: { y: 40 } },
        { name: "fade-left", group: "group-fade-left", from: { x: -50 } },
        { name: "fade-right", group: "group-fade-right", from: { x: 50 } }
    ];

    animations.forEach(({ name, group: groupName, from }) => {

        const initializedClass = `${name}-initialized`;
        const itemSelector = `.${name}:not(.${initializedClass})`;
        let groups = Array.from(scope.querySelectorAll(`.${groupName}`));

        if (scope.classList && scope.classList.contains(groupName)) {
            groups.unshift(scope);
        }

        groups.forEach((group) => {

            const items = Array.from(group.querySelectorAll(itemSelector))
                .filter((item) => item.closest(`.${groupName}`) === group);

            if (!items.length) return;

            items.forEach((item) => item.classList.add(initializedClass));

            createReveal(items, group, from, {
                stagger: 0.2,
                playNow: animateNow
            });
        });

        const singles = Array.from(scope.querySelectorAll(itemSelector));

        if (scope.matches && scope.matches(itemSelector)) {
            singles.unshift(scope);
        }

        singles
            .filter((item) => !item.closest(`.${groupName}`))
            .forEach((item) => {

                item.classList.add(initializedClass);

                createReveal([item], item, from, { playNow: animateNow });
            });
    });

    if (!animateNow && !prefersReducedMotion()) {
        ScrollTrigger.refresh();
    }
};

/**
 * Numbered process steps (layout_path).
 */
window.initPathAnimations = function (scope = document) {

    const selector = ".path__steps:not(.path-initialized)";

    const paths = Array.from(scope.querySelectorAll(selector));

    if (scope.matches && scope.matches(selector)) {
        paths.unshift(scope);
    }

    if (!paths.length) return;

    const reduced = prefersReducedMotion();

    paths.forEach((path) => {

        path.classList.add("path-initialized");

        const steps = gsap.utils.toArray(path.querySelectorAll(".path__step"));
        const lines = path.querySelectorAll(".path__step-line");

        if (!steps.length) return;

        if (reduced) {
            gsap.set(steps, { opacity: 1 });

            if (lines.length) {
                gsap.set(lines, { scaleX: 1 });
            }

            return;
        }

        gsap.set(steps, { opacity: 0.2 });

        if (lines.length) {
            gsap.set(lines, { scaleX: 0, transformOrigin: "left center" });
        }

        let played = false;

        const play = function () {

            if (played) return;

            played = true;

            const index = pendingReveals.indexOf(entry);

            if (index > -1) {
                pendingReveals.splice(index, 1);
            }

            const tl = gsap.timeline();

            steps.forEach((step) => {

                const line = step.querySelector(".path__step-line");

                tl.to(step, { opacity: 1, duration: 0.35, ease: "power2.out" });

                if (line) {
                    tl.to(line, { scaleX: 1, duration: 0.45, ease: "power2.inOut" }, "-=0.1");
                }
            });
        };

        const entry = { trigger: path, play: play };

        pendingReveals.push(entry);

        ScrollTrigger.create({
            trigger: path,
            start: "top bottom",
            once: true,
            onEnter: play
        });
    });
};

/**
 * Card grids. Also used for AJAX-inserted cards - pass the new nodes as the
 * scope and animateNow as true, since by then the grid is already on screen.
 */
function initCardReveal(scope, animateNow) {

    scope = scope || document;

    const grids = Array.from(scope.querySelectorAll("[data-gsap-cards]"));

    if (scope.matches && scope.matches("[data-gsap-cards]")) {
        grids.unshift(scope);
    }

    if (!grids.length) return;

    grids.forEach(function (grid) {

        const cards = Array.from(grid.children).filter(function (card) {
            return !card.classList.contains("cards-initialized");
        });

        if (!cards.length) return;

        cards.forEach(function (card) {
            card.classList.add("cards-initialized");
        });

        grid.classList.add("cards-initialized");

        createReveal(cards, grid, { y: 40 }, {
            duration: 0.6,
            stagger: 0.08,
            start: "top bottom-=80",
            playNow: animateNow
        });
    });
}

window.initCardReveal = initCardReveal;

function initializeAboveFoldAnimations() {

    document.querySelectorAll("[data-animate-now]").forEach((scope) => {
        window.initFadeUpAnimations(scope, true);
    });
}

function initializeScrollAnimations() {

    initializeAboveFoldAnimations();

    window.initFadeUpAnimations(document);
    window.initPathAnimations(document);
    window.initCardReveal(document);

    ScrollTrigger.refresh();

    /*
     * Only now is the CSS allowed to keep hiding things: the flag says the
     * bundle ran to completion, not merely that the file started executing.
     */
    window.__animationsReady = true;

    /* The page may already be restored mid-document at this point. */
    revealVisiblePending();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeScrollAnimations);
} else {
    initializeScrollAnimations();
}

/**
 * Trigger positions are measured before images and webfonts finish loading,
 * so they have to be recalculated once the page has settled.
 */
window.addEventListener("load", () => {
    ScrollTrigger.refresh();
    revealVisiblePending();
});

if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
        ScrollTrigger.refresh();
        revealVisiblePending();
    });
}

/**
 * Recovery after the page comes back from the background or from bfcache.
 *
 * Returning from another app changes the viewport height (address bar) and
 * restores the scroll position, both of which can leave ScrollTrigger holding
 * measurements that no longer match the document. Re-measure, then reveal
 * whatever is already on screen so nothing can stay stuck at opacity 0.
 */
function recoverAfterRestore() {

    if (document.hidden) return;

    ScrollTrigger.refresh();

    /* Two frames: let the address bar settle before reading positions. */
    requestAnimationFrame(function () {
        requestAnimationFrame(function () {
            ScrollTrigger.refresh();
            revealVisiblePending();
        });
    });
}

document.addEventListener("visibilitychange", recoverAfterRestore);
window.addEventListener("pageshow", recoverAfterRestore);
window.addEventListener("orientationchange", recoverAfterRestore);